/* NAV adófolyószámla — a NAV-tól kapott adat MEGTEKINTÉSE.
 *
 * ⚠⚠ EZ NEM AZ ONLINE SZÁMLA. Az a bejövő számlák letöltése; ez az
 * adófolyószámla-tükör (M2M Adózó API).
 *
 * MIÉRT KÜLÖN OLDAL A „NAV EGYEZTETÉS" MELLETT: az egyeztetés a könyvelői és a
 * NAV-adat KÜLÖNBSÉGÉT mutatja, tehát csak azt látod, ami eltér — ami rendben
 * van, az nem jelenik meg. A NAV folyószámla önmagában is információ
 * (tartozás/túlfizetés adónemenként, esedékességi naptár, közlemények), és a
 * felhasználónak látnia kell akkor is, ha nincs mihez hasonlítani.
 *
 * HÁROM DOLOG, AMI ITT SZÁNDÉKOS:
 *
 * 1. Az EGYENLEG mellett MINDIG ott van a MINŐSÍTÉS. A NAV-nál az irányt
 *    (tartozás/túlfizetés) a `minosites` hordozza, NEM az összeg előjele — a
 *    +18 000 Ft is lehet túlfizetés. Előjelre színezni itt valódi félrevezetés
 *    lenne (`.claude/rules/nav-upo.md` 2./#2).
 * 2. Az „eltűnt" tételeket NEM rejtjük el, csak áthúzzuk. Azt jelenti, a NAV
 *    kivette a válaszából — ez maga is információ, és ha eltüntetnénk, egy
 *    lekönyvelt vagy visszavont előírás nyom nélkül eltűnne a felületről.
 * 3. Az adóalany-azonosító KI VAN ÍRVA a fejlécben. Teszt-környezetben a
 *    rendszer a NAV anonimizált teszt-adózóit kérdezi, tehát a látott adat NEM a
 *    saját cégünk adata — enélkül a nézet félrevezető lenne.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ⚠ HÁROM UX-DÖNTÉS Gábor 2026-08-05-i visszajelzéséből („ez így most nagyon
 * kusza oldal lett… nem fér ki horizontális scroll nélkül"). Ne „javítsd vissza":
 *
 * a) **ÜRES OSZLOPOT NEM RAJZOLUNK** (`col` / `adCol`). A NAV a tételek
 *    többségén nem ad közleményt/bizonylatszámot, az állapot-jelzők pedig csak
 *    összevetés után keletkeznek. Végig „—" oszlopokból lett a vízszintes
 *    csúszka. A számítás a LÁTHATÓ oldalra vonatkozik — ha a következő oldalon
 *    van adat, ott megjelenik.
 *
 * b) **A NYITÓEGYENLEGEK ÖSSZECSUKVA.** Az éles adatban 15 tételből 12 volt
 *    nyitóegyenleg, és egyik sem fizetendő — kifejtve elnyomták azt a hármat,
 *    ami érdekes. ⚠ Az összegük a csoport-soron LÁTSZIK, tehát nem rejtünk el
 *    adatot, csak nem ismételjük 12-szer ugyanazt a magyarázatot.
 *
 * c) **NINCS „Fajta" oszlop és szűrő** („értelmezhetetlen nekem"). A
 *    `tétel` / `könyvelésre vár` a felhasználó szempontjából egyetlen dolgot
 *    jelent — „a NAV még nem könyvelte le" —, és azt a megnevezés alatt írjuk
 *    ki. A `tipus_kod` a PÉNZFORGALOM fajtája („Devizabefizetés"), ezért az
 *    összeg alá került, nem külön oszlopba.
 *
 * d) **Az ADÓNEM: `Név (kód)` EGY SORBAN**, nem két sorban (Gábor, 2026-08-05:
 *    „legyen zárójelben az adónem megnevezése után… Ezzel így ez a táblázat is
 *    1 soros lehet"). A név az elsődleges, a kód halványabb (`--text-muted`) —
 *    de NEM kisebb: a számot le kell tudni olvasni. Egy „950" önmagában
 *    senkinek nem mond semmit, egy külön sor viszont minden sort megduplázott.
 *    Ugyanez a forma a tétel- ÉS az adónem-táblában. Hosszú név: `Truncate`
 *    (csonkolás + tooltip), de a KÓD sosem csonkolódik (`flexShrink: 0`).
 *
 * e) **`tableLayout: 'fixed'` + `colgroup` MINDKÉT táblán.** Auto-layout mellett a
 *    kolumna-szélesség a TARTALOMTÓL függ, tehát a nyitóegyenlegek kifejtése
 *    ÁTMÉRETEZTE az összes oszlopot — Gábor két képernyőképe pontosan ezt mutatta.
 *    Fix layouttal a szélesség állandó, és a `Truncate` is kiszámíthatóan csonkol.
 *
 * f) **Táblázat-cellában NINCS `Btn`.** Annak a padding-ja ~10 px-szel magasabbá
 *    tette a sort, mint a szöveg — attól látszott az adónem-tábla minden sora
 *    „2 sorosnak". Helyette `LinkBtn` (valódi `<button>`, szöveg-magasságú).
 *    Az adónem-tábla ezért `tdMid`-et használ (`verticalAlign: middle`), a
 *    tétel-tábla `td`-t (`top`), mert ott van értelmes második sor.
 *
 * g) **A „legutóbbi hibás hívás" figyelmeztetés NINCS ezen a lapon.** Ez a szekció
 *    az ADATOT mutatja, nem a technikai hívás-állapotot; a hibák helye a
 *    hívásnapló (Beállítások → NAV adófolyószámla), ahol letölthetők is.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Search, AlertTriangle, Coins, ExternalLink,
         ChevronDown, ChevronRight } from 'lucide-react';
import { navUpoApi } from '../services/api';
import { Btn, Card, Alert, Amount, Input, Select, Badge, Spinner, Checkbox,
         StatCard, Truncate } from '../components/UI';
/* A compliance (hiányzó bevallás · köztartozás · KOMA) KÜLÖN komponens: más
 * NAV-végpontok, más táblák — de ugyanarra a kérdésre válaszol („mit tud rólunk
 * a NAV"), ezért ül egy lapon. Külön fájlban, mert ez az oldal már így is hosszú. */
import NavCompliance from '../components/NavCompliance';

const fmtDate = (v) => v ? v.replace(/-/g, '.') + '.' : '—';
const fmtTs = (iso) => iso
  ? new Date(iso).toLocaleString('hu-HU', { timeZone: 'Europe/Budapest',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit' })
  : '—';

/* A NAV minősítései emberi szóval + színnel. ⚠ A színt a MINŐSÍTÉS adja, nem az
 * összeg előjele — lásd a fájl fejlécének 1. pontját. */
const MINOSITES = {
  TARTOZAS:    { label: 'tartozás',    color: 'var(--danger)' },
  TULFIZETES:  { label: 'túlfizetés',  color: 'var(--success)' },
  NULLA:       { label: 'nulla',       color: 'var(--text-muted)' },
};

const th = {
  textAlign: 'left', padding: '0.4rem 0.6rem', fontSize: 'var(--fs-xs)',
  fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase',
  letterSpacing: '0.05em', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};
/* ⚠ KÉT td-variáns, és ez nem szőrszálhasogatás:
   - `td` (verticalAlign: top) a TÉTEL-táblához, ahol egy cellában lehet második
     sor (teljesítési dátum, „a NAV még nem könyvelte le", deviza-árfolyam);
   - `tdMid` (middle) az ADÓNEM-táblához, ami EGYSOROS. Ott a `top` azt csinálta,
     hogy a magasabb sorban a szöveg felülre ugrott — Gábor: „még mindig 2 soros,
     csak felülre van rendezve". A magasságot ott a `Btn` adta, lásd lentebb. */
const td = {
  padding: '0.42rem 0.6rem', fontSize: '0.8rem',
  borderBottom: '1px solid var(--border-subtle, var(--border))',
  verticalAlign: 'top',
};
const tdMid = { ...td, verticalAlign: 'middle' };

/* Szöveg-gomb: úgy viselkedik, mint egy link, de VALÓDI <button> (billentyűzet +
   képernyőolvasó). ⚠ Azért nem a `Btn` komponens: annak a padding-ja ~10 px-szel
   magasabbá tette a táblázat-sort, mint a szöveg — attól látszott „2 sorosnak"
   az adónem-tábla minden sora. Táblázat-cellában szöveg-magasságú vezérlő kell. */
function LinkBtn({ children, onClick, active }) {
  return (
    <button type="button" onClick={onClick} style={{
      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
      font: 'inherit', color: active ? 'var(--text)' : 'var(--primary)',
      fontWeight: active ? 600 : 400, textDecoration: 'underline',
      textUnderlineOffset: 2,
    }}>{children}</button>
  );
}

function Minosites({ minosites }) {
  const m = MINOSITES[minosites] || { label: minosites || '—',
                                      color: 'var(--text-muted)' };
  return <span style={{fontSize:'0.7rem',fontWeight:600,color:m.color}}>{m.label}</span>;
}

export default function NavTaxAccount() {
  const [adonemKod, setAdonemKod] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [hideVanished, setHideVanished] = useState(false);
  /* ⚠ ALAPBÓL az éppen lekérdezett adóalanyra szűrünk. A tétel-tábla több
   * teszt-adózó adatát is tartalmazhatja (a sandboxban váltogatunk), a fej-adat
   * viszont EGY adózó snapshotja — szűrés nélkül a „tartozás" és a tétel-lista
   * két különböző cégről szólna. Kikapcsolható, mert a többi adózó adata is
   * megvan, és néha épp azt akarjuk látni. */
  const [minden, setMinden] = useState(false);
  // A nyitóegyenlegek alapból ÖSSZECSUKVA: az éles adatban ezek a tételek
  // többsége, és egyik sem fizetendő. Az összegük a csoport-soron látszik,
  // tehát nem rejtünk el adatot.
  const [nyitoOpen, setNyitoOpen] = useState(false);
  const [sort, setSort] = useState('esedekesseg');
  const [offset, setOffset] = useState(0);
  const LIMIT = 100;

  const { data: view = {}, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['nav-upo-view'], queryFn: navUpoApi.view,
  });
  const { data: list = {}, isLoading: listLoading } = useQuery({
    // ⚠ A `view` betöltése után újra fut: az `adoalany` onnan jön.
    enabled: view.configured !== undefined,
    queryKey: ['nav-upo-items', adonemKod, search, onlyOpen,
               hideVanished, sort, offset, minden, view.adoalany_azonosito],
    queryFn: () => navUpoApi.items({
      adonem_kod: adonemKod || undefined,
      search: search || undefined,
      only_open: onlyOpen || undefined,
      include_vanished: !hideVanished,
      adoalany: minden ? undefined : (view.adoalany_azonosito || undefined),
      sort, limit: LIMIT, offset,
    }),
  });

  const snap = view.snapshot;
  const adonemek = view.adonemek || [];
  const items = list.items || [];
  const total = list.total || 0;

  /* ── Kód → adónem-NÉV. A „950" önmagában senkinek nem mond semmit; a nevet a
     fej-adat adónem-bontása adja (ugyanaz a NAV-válasz). */
  const adonemNev = Object.fromEntries(
    adonemek.filter(a => a.megnevezes).map(a => [a.adonem_kod, a.megnevezes]));

  /* ── ÜRES OSZLOPOKAT NEM RAJZOLUNK ki.
     A NAV a tételek többségén nem ad közleményt/bizonylatszámot, és az állapot-
     jelzők is csak összevetés után keletkeznek. Három végig „—" oszlop egyrészt
     zaj, másrészt vízszintes csúszkát okoz — Gábor visszajelzése (2026-08-05):
     „látok közlemény/bizonylat mezőt és állapotot, de minden üres… és nem fér
     ki horizontális scroll nélkül".
     ⚠ A döntés a LÁTHATÓ oldalra vonatkozik: ha a következő oldalon van adat,
     ott meg fog jelenni. Ez szándékos — az alternatíva (a teljes találati
     halmaz vizsgálata) egy külön lekérdezés lenne ugyanezért. */
  const col = {
    penzforgalom: items.some(i => i.penzforgalom != null || i.deviza),
    kozlemeny: items.some(i => i.kozlemeny || i.bizonylat_szama
                            || i.hatarozatszam),
    allapot: items.some(i => i.is_open || i.settled_at || i.vanished_at
                          || i.document_id),
  };

  /* Ugyanez az elv az adónem-táblára: a „könyvelésre vár" a legtöbb adónemen 0,
     és egy végig nullás oszlop csak szélesíti a táblát. */
  const adCol = {
    kotelezettseg: adonemek.some(a => a.kotelezettseg_osszesen),
    varo: adonemek.some(a => a.konyvelesre_varo_osszesen),
    szamlaszam: adonemek.some(a => a.szamlaszam),
  };

  /* ── A nyitóegyenlegek külön, összecsukva (lásd a táblázatnál). */
  const nyitok = items.filter(i => i.is_opening_balance);
  const egyebek = items.filter(i => !i.is_opening_balance);
  const nyitoOsszeg = nyitok.reduce((s, i) => s + (i.kotelezettseg || 0), 0);

  // Szűrő-váltásnál vissza az első oldalra — különben egy 3. oldalon állva egy
  // szűkítő szűrő üres listát adna, és az „nincs adat"-nak látszana.
  const setFilter = (fn) => (v) => { fn(v); setOffset(0); };

  if (isLoading) return <Spinner/>;

  if (!view.configured) {
    return (
      <Card title="NAV adófolyószámla">
        <Alert type="info">
          Még nem futott adófolyószámla-lekérdezés. Állítsd be a hozzáférést
          (Beállítások → NAV adófolyószámla), majd futtass egy szinkront.
        </Alert>
      </Card>
    );
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>

      {/* ── Fejléc: KI adatait látjuk, és mikorról ── */}
      <Card title="NAV adófolyószámla"
        subtitle="A NAV Adózó API-ból (M2M) letöltött folyószámla-tükör. Ez NEM az Online Számla — az a bejövő számlák letöltése."
        actions={
          <Btn size="sm" variant="ghost" onClick={() => refetch()}
            loading={isFetching}><RefreshCw size={14}/>Frissítés</Btn>}>

        {/* ⚠ Teszt-környezetben MÁS adózó adatait mutatjuk — ezt ki KELL írni. */}
        {view.environment !== 'production' && (
          <Alert type="warning" style={{marginBottom:12}}>
            <b>TESZT környezet.</b> A látott adat a NAV anonimizált
            teszt-adózójától jön
            {view.adoalany_azonosito && <> (<b style={{fontFamily:'var(--mono)'}}>
              {view.adoalany_azonosito}</b>{view.adoalany_nev
                ? <> — {view.adoalany_nev}</> : null})</>}
            , <b>nem a saját cégünk</b> valós adófolyószámlája. A lekérdezett
            adózó a Beállítások → NAV adófolyószámla panelen váltható.
          </Alert>
        )}

        <div style={{display:'grid',gap:12,
          gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',marginBottom:12}}>
          {/* review I1 (2026-09-28): ≤ 2 munkavállalónál munkatársnak a végösszegek bér nélkül */}
          <StatCard label={snap?.pay_hidden ? 'Adózói tartozás (bér nélkül)' : 'Adózói tartozás'} color="var(--danger)"
            value={<Amount value={snap?.tartozas ?? 0}/>}/>
          <StatCard label={snap?.pay_hidden ? 'Túlfizetés (bér nélkül)' : 'Túlfizetés'} color="var(--success)"
            value={<Amount value={snap?.tulfizetes ?? 0}/>}/>
          <StatCard label={snap?.pay_hidden ? 'Könyveletlen (bér nélkül)' : 'Könyveletlen'} color="var(--warning)"
            value={<Amount value={snap?.konyveletlen_osszesen ?? 0}/>}
            sub="a NAV még nem könyvelte le"/>
          <StatCard label="Nyitott tételek" value={view.nyitott_tetelek ?? 0}
            sub={`${view.osszes_tetel ?? 0} tétel ennél az adózónál`
              + (view.osszes_tetel_minden_adoalany > view.osszes_tetel
                 ? ` (${view.osszes_tetel_minden_adoalany} összesen)` : '')}/>
        </div>

        <div style={{display:'flex',gap:16,flexWrap:'wrap',fontSize:'0.74rem',
          color:'var(--text-muted)'}}>
          <span>NAV lekérdezés: <b>{fmtTs(snap?.nav_lekerdezes_idopontja)}</b></span>
          <span>letöltve: <b>{fmtTs(snap?.queried_at)}</b></span>
          <span>időszak vége: <b>{fmtDate(snap?.idoszak_vege)}</b></span>
          {snap?.van_eloremutato_tetel && (
            <Badge variant="warning">előremutató tétel is van</Badge>)}
          {view.eltunt_tetelek > 0 && (
            <span>a NAV kivett a válaszából: <b>{view.eltunt_tetelek}</b> tétel</span>)}
        </div>

        {/* ⚠ A „legutóbbi hibás hívás" figyelmeztetés SZÁNDÉKOSAN NINCS itt
            (Gábor, 2026-08-05): ez a szekció az ADATOT mutatja, nem a technikai
            hívás-állapotot. A hibák helye a hívásnapló (Beállítások → NAV
            adófolyószámla), ahol letölthetők is — a NAV 6.D elvárása így is
            teljesül. Ne tedd vissza: egy régi, már megoldott hiba sárga mezője a
            lap tetején azt sugallja, hogy a látott adat hibás. */}
      </Card>

      {/* ── Bevallások és köztartozás (9. lépés) ── */}
      <NavCompliance/>

      {/* ── Adónem-bontás: EZ a legértékesebb nézet ─────────────────────────
          A `szamlaszam` azért van itt, mert az az UTALÁS CÉLSZÁMLÁJA, és a
          cég SAJÁT válaszából jön (soha nem a globális szótárból). Ahol a NAV
          nem adott számot, ott ezt kiírjuk — nem tippelünk. */}
      <Card title={`Adónemek (${adonemek.length})`}
        subtitle="Adónemenkénti egyenleg és a NAV bankszámlaszáma. Az irányt a minősítés adja, nem az összeg előjele.">
        {adonemek.length === 0 ? (
          <Alert type="info">Nincs adónem-bontás. Futtass tételes lekérdezést
            (a Szinkronizálás panelen „Tételes adószámla").</Alert>
        ) : (
          <div style={{overflowX:'auto'}}>
            {/* ⚠⚠ `tableLayout: 'fixed'` + `colgroup`, és ez NEM kozmetika.
                Auto-layout mellett a kolumna-szélesség a TARTALOMTÓL függ, tehát a
                nyitóegyenlegek kifejtésekor MINDEN oszlop átméretezett — Gábor
                két képernyőképe pontosan ezt mutatta („a táblázat megjelenítése
                rossz"): összecsukva más szélességek, kinyitva mások. Fix layouttal
                a szélesség állandó, és a `Truncate` is kiszámíthatóan csonkol. */}
            <table style={{width:'100%',borderCollapse:'collapse',minWidth:700,
              tableLayout:'fixed'}}>
              <colgroup>
                <col/>
                <col style={{width:130}}/>
                <col style={{width:104}}/>
                {adCol.kotelezettseg && <col style={{width:130}}/>}
                {adCol.varo && <col style={{width:130}}/>}
                {adCol.szamlaszam && <col style={{width:172}}/>}
                <col style={{width:86}}/>
              </colgroup>
              <thead><tr>
                {/* A KÓD nem külön oszlop: a név alatt, halványan. Önmagában
                    egy „950" senkinek nem mond semmit, viszont egy teljes oszlopot
                    vitt el. */}
                <th style={th}>Adónem</th>
                <th style={{...th,textAlign:'right'}}>Egyenleg</th>
                <th style={th}>Minősítés</th>
                {adCol.kotelezettseg && (
                  <th style={{...th,textAlign:'right'}}>Kötelezettség</th>)}
                {adCol.varo && (
                  <th style={{...th,textAlign:'right'}}>Könyvelésre vár</th>)}
                {adCol.szamlaszam && <th style={th}>NAV bankszámlaszám</th>}
                <th style={{...th,textAlign:'right'}}>Tétel</th>
              </tr></thead>
              <tbody>
                {adonemek.map(a => (
                  <tr key={a.adonem_kod}
                    style={{background: adonemKod === a.adonem_kod
                      ? 'var(--bg-hover, rgba(59,130,246,0.08))' : undefined}}>
                    {/* ⚠ A kód ZÁRÓJELBEN a név után, NEM új sorban (Gábor,
                        2026-08-05): így a táblázat sorai egysorosak maradnak. A
                        név az elsődleges, a kód halványabb — de NEM kisebb, mert
                        a szám leolvasható legyen (lásd a kontraszt-javítást). */}
                    {/* ⚠ A KÓD flex-elemként `flexShrink: 0` — a hosszú NÉV
                        csonkolódik, a szám SOHA. Egy levágott adónem-kód
                        használhatatlan lenne (abból lesz az utalás célszámlája). */}
                    <td style={tdMid}>
                      <div style={{display:'flex',gap:4,alignItems:'baseline'}}>
                        <Truncate>{a.megnevezes || '—'}</Truncate>
                        <span style={{color:'var(--text-muted)',flexShrink:0}}>
                          ({a.adonem_kod})</span>
                      </div>
                    </td>
                    <td style={{...tdMid,textAlign:'right'}}>
                      <Amount value={a.egyenleg ?? 0}/></td>
                    <td style={tdMid}><Minosites minosites={a.minosites}/></td>
                    {adCol.kotelezettseg && (
                      <td style={{...tdMid,textAlign:'right'}}>
                        <Amount value={a.kotelezettseg_osszesen ?? 0}/></td>)}
                    {adCol.varo && (
                      <td style={{...tdMid,textAlign:'right'}}>
                        <Amount value={a.konyvelesre_varo_osszesen ?? 0}/></td>)}
                    {adCol.szamlaszam && (
                      <td style={{...tdMid,fontFamily:'var(--mono)',fontSize:'0.74rem'}}>
                        {a.szamlaszam || (
                          <span style={{color:'var(--warning)',
                            display:'inline-flex',alignItems:'center',gap:4}}>
                            <AlertTriangle size={12}/>a NAV nem adott számot
                          </span>)}
                      </td>)}
                    <td style={{...tdMid,textAlign:'right'}}>
                      {a.tetel_db > 0 ? (
                        <LinkBtn active={adonemKod === a.adonem_kod}
                          onClick={() => setFilter(setAdonemKod)(
                            adonemKod === a.adonem_kod ? '' : a.adonem_kod)}>
                          {a.tetel_db} tétel
                        </LinkBtn>
                      ) : <span style={{color:'var(--text-muted)'}}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {view.adonem_snapshot_id && snap && view.adonem_snapshot_id !== snap.id && (
          <p style={{fontSize:'0.72rem',color:'var(--text-dim)',margin:'8px 0 0'}}>
            A bontás egy korábbi lekérdezésből való (a legutolsó szinkron
            változatlan payloadot kapott, ezért nem írt új adónem-réteget).
          </p>
        )}
      </Card>

      {/* ── Tételek ── */}
      <Card title={`Tételek (${total})`}
        subtitle="A NAV folyószámla egyes tételei: előírások, befizetések, könyvelésre váró tételek. Devizás tételnél az eredeti összeg és az árfolyam is látszik.">

        <div style={{display:'flex',gap:8,alignItems:'end',flexWrap:'wrap',
          marginBottom:12}}>
          <Select label="Adónem" value={adonemKod}
            onChange={e => setFilter(setAdonemKod)(e.target.value)}
            options={[{ value: '', label: 'mind' },
              ...adonemek.map(a => ({ value: a.adonem_kod,
                label: `${a.adonem_kod} · ${a.megnevezes || ''}` }))]}
            style={{minWidth:200}}/>
          {/* ⚠ A „Fajta" szűrő KIMARAD (Gábor: „a Fajta oszlop értelmezhetetlen
              nekem"). A `tétel` / `könyvelésre vár` megkülönböztetés a felhasználó
              szempontjából egyetlen dolgot jelent — „a NAV még nem könyvelte le" —,
              és azt a megnevezés alatt írjuk ki, ott, ahol számít. */}
          <Select label="Rendezés" value={sort}
            onChange={e => setFilter(setSort)(e.target.value)}
            options={[{ value: 'esedekesseg', label: 'esedékesség' },
              { value: 'osszeg', label: 'összeg' },
              { value: 'adonem', label: 'adónem' },
              { value: 'latott', label: 'utoljára látott' }]}/>
          <form onSubmit={e => { e.preventDefault();
            setSearch(searchDraft); setOffset(0); }}
            style={{display:'flex',gap:6,alignItems:'end'}}>
            <Input label="Közlemény / bizonylatszám" value={searchDraft}
              placeholder="keresés…"
              onChange={e => setSearchDraft(e.target.value)}
              style={{maxWidth:200}}/>
            <Btn size="sm" variant="ghost" type="submit"><Search size={14}/></Btn>
          </form>
          <div style={{display:'flex',flexDirection:'column',gap:4,paddingBottom:4}}>
            {/* ⚠ A `Checkbox` az ESEMÉNYT adja tovább, nem a boolean-t. */}
            <Checkbox label="csak nyitott" checked={onlyOpen}
              onChange={e => setFilter(setOnlyOpen)(e.target.checked)}/>
            <Checkbox label="eltűntek elrejtése" checked={hideVanished}
              onChange={e => setFilter(setHideVanished)(e.target.checked)}/>
            {view.adoalany_azonosito
              && view.osszes_tetel_minden_adoalany > view.osszes_tetel && (
              <Checkbox label="minden adóalany tételei" checked={minden}
                onChange={e => setFilter(setMinden)(e.target.checked)}/>
            )}
          </div>
        </div>

        {listLoading ? <Spinner/> : items.length === 0 ? (
          <Alert type="info">Nincs a szűrőnek megfelelő tétel.</Alert>
        ) : (
          <div style={{overflowX:'auto'}}>
            {/* Ugyanaz a fix layout, ugyanabból az okból (lásd az adónem-tábla
                kommentjét): a csoport kifejtése ne méretezze át az oszlopokat. */}
            <table style={{width:'100%',borderCollapse:'collapse',minWidth:640,
              tableLayout:'fixed'}}>
              {/* ⚠ AZ ARÁNYOK A LÉNYEG, nem csak a fix layout. Az első próbámban a
                  KÖZLEMÉNY 16%-ot (~300 px) kapott, hogy egy „—"-t mutasson,
                  MIKÖZBEN emiatt csonkolódtak az adónem-nevek. A szabály: a
                  RITKÁN kitöltött oszlopok SZŰKEK és fix px-esek, a hosszú szöveget
                  hordozó ADÓNEM kapja a maradékot (`<col/>` szélesség nélkül). */}
              <colgroup>
                <col style={{width:104}}/>
                <col/>
                <col style={{width:'24%'}}/>
                <col style={{width:128}}/>
                {col.penzforgalom && <col style={{width:168}}/>}
                {col.kozlemeny && <col style={{width:124}}/>}
                {col.allapot && <col style={{width:124}}/>}
              </colgroup>
              <thead><tr>
                <th style={th}>Esedékesség</th>
                <th style={th}>Adónem</th>
                {/* ⭐ EZ MONDJA MEG, MI A SOR (a NAV `megnevezes` mezője). */}
                <th style={th}>Megnevezés</th>
                <th style={{...th,textAlign:'right'}}>Kötelezettség</th>
                {/* ⚠ A többi oszlop CSAK akkor jelenik meg, ha van benne adat —
                    lásd a `col` számítást. A NAV a legtöbb tételen nem ad
                    közleményt/bizonylatszámot, és három üres oszlop egyrészt
                    zaj, másrészt vízszintes csúszkát okoz. */}
                {col.penzforgalom && (
                  <th style={{...th,textAlign:'right'}}>Pénzforgalom</th>)}
                {col.kozlemeny && <th style={th}>Közlemény / bizonylat</th>}
                {col.allapot && <th style={th}>Állapot</th>}
              </tr></thead>
              <tbody>
                {/* ── A nyitóegyenlegek ÖSSZECSUKVA ─────────────────────────
                    Az éles adatban a tételek TÖBBSÉGE nyitóegyenleg (15-ből 12),
                    és egyik sem fizetendő — kifejtve elnyomják azt a néhány
                    sort, ami tényleg érdekes. Az összeg viszont látszik, tehát
                    nem rejtünk el adatot, csak nem ismételjük 12-szer ugyanazt. */}
                {nyitok.length > 0 && (
                  <tr onClick={() => setNyitoOpen(v => !v)}
                    style={{cursor:'pointer',background:'var(--bg-hover, rgba(148,163,184,0.06))'}}>
                    <td style={{...td,whiteSpace:'nowrap'}} colSpan={2}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:5,
                        fontSize:'0.76rem',fontWeight:600}}>
                        {nyitoOpen ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                        Nyitóegyenlegek ({nyitok.length})
                      </span>
                    </td>
                    <td style={{...td,fontSize:'0.74rem',color:'var(--text-muted)'}}>
                      áthozott egyenleg — <b>nem fizetendő</b>
                    </td>
                    <td style={{...td,textAlign:'right',fontWeight:600}}>
                      <Amount value={nyitoOsszeg}/>
                    </td>
                    {col.penzforgalom && <td style={td}/>}
                    {col.kozlemeny && <td style={td}/>}
                    {col.allapot && <td style={td}/>}
                  </tr>
                )}

                {(nyitoOpen ? nyitok : []).concat(egyebek).map(i => (
                  <tr key={i.id} style={{
                    // ⚠ Nem elrejtve, csak áthúzva: a NAV kivette a válaszából.
                    opacity: i.vanished_at ? 0.5
                             : i.is_opening_balance ? 0.65 : 1,
                    textDecoration: i.vanished_at ? 'line-through' : undefined}}>
                    <td style={{...td,whiteSpace:'nowrap'}}>
                      {fmtDate(i.esedekessegi_datum)}
                      {i.teljesitesi_datum && (
                        <div style={{fontSize:'var(--fs-xs)',color:'var(--text-dim)'}}>
                          teljesítés: {fmtDate(i.teljesitesi_datum)}</div>)}
                    </td>
                    {/* ⚠ Az adónem NEVE az elsődleges, a KÓD zárójelben utána —
                        ugyanaz a forma, mint az adónem-táblában (egysoros sor).
                        A „950" önmagában senkinek nem mond semmit; a nevet a
                        fej-adat adónem-bontásából oldjuk fel. */}
                    <td style={td}>
                      <div style={{display:'flex',gap:4,alignItems:'baseline'}}>
                        <Truncate>{adonemNev[i.adonem_kod] || 'ismeretlen adónem'}</Truncate>
                        <span style={{color:'var(--text-muted)',flexShrink:0}}>
                          ({i.adonem_kod}{i.aladonem ? `/${i.aladonem}` : ''})</span>
                      </div>
                    </td>
                    <td style={td}>
                      {i.megnevezes
                        ? <Truncate>{i.megnevezes}</Truncate>
                        : <span style={{color:'var(--text-muted)'}}>—</span>}
                      {/* A „könyvelésre vár" a NAV-nál MÉG NEM könyvelt tétel —
                          csak akkor írjuk ki, ha tényleg az (nem külön oszlopban,
                          mert a tételek túlnyomó többsége nem ilyen). */}
                      {i.item_kind === 'KONYVELESRE_VARO' && (
                        <div style={{fontSize:'var(--fs-xs)',color:'var(--warning)',
                          marginTop:2}}>a NAV még nem könyvelte le</div>)}
                      {/* Az ÁTVEZETÉS az a rendezés, amihez nincs banki utalás. */}
                      {(i.kotelezettseg_atvezetve_forras
                        || i.penzforgalom_atvezetve_forras) && (
                        <div style={{fontSize:'var(--fs-xs)',color:'var(--primary)',
                          marginTop:2}}>
                          átvezetés: {i.kotelezettseg_atvezetve_forras
                                      || i.penzforgalom_atvezetve_forras}
                        </div>)}
                    </td>
                    <td style={{...td,textAlign:'right'}}>
                      {i.kotelezettseg == null
                        ? <span style={{color:'var(--text-dim)'}}>—</span>
                        : <Amount value={i.kotelezettseg}/>}
                    </td>
                    {col.penzforgalom && (
                      <td style={{...td,textAlign:'right'}}>
                        {/* ⚠ ÜRES cella = ÜRES, nem „—". A KÖTELEZETTSÉG (a fő
                            pénzügyi oszlop) megtartja a „—"-t, mert ott az explicit
                            „nincs összeg" információ; itt viszont 12 soron át
                            ismétlődő „— —" pár volt, ami csak zaj. */}
                        {i.penzforgalom == null ? null
                          : <Amount value={i.penzforgalom}/>}
                        {/* ⚠ A pénzforgalom-fajta („Devizabefizetés") ÉS a deviza
                            EGY sorba, nem kettőbe: külön sorokban a devizás tételek
                            HÁROM sor magasak lettek, a többi meg egy — a táblázat
                            ettől „ugrált". A `tipus_kod` a PÉNZFORGALOM fajtája,
                            ezért itt van és nem külön oszlopban; a nyers numerikus
                            kódot csak akkor írjuk ki, ha a NAV nem adott emberi
                            alakot hozzá. */}
                        {(i.deviza || i.tipus_kodolatlan || i.tipus_kod) && (
                          <div style={{fontSize:'var(--fs-xs)',
                            color:i.deviza ? 'var(--primary)' : 'var(--text-muted)',
                            display:'flex',alignItems:'center',gap:3,
                            justifyContent:'flex-end',flexWrap:'wrap'}}>
                            {i.deviza && <Coins size={11}/>}
                            {/* ⚠ Devizás tételnél a fajta („Devizabefizetés") a
                                TOOLTIPBE megy, nem tűnik el: a szűk oszlopban
                                mindkettő kiírva sortörést okozna, az árfolyam
                                viszont a fontosabb (abból lesz a forintosítás). */}
                            <span title={i.tipus_kodolatlan
                                         || (i.tipus_kod ? `típus ${i.tipus_kod}` : undefined)}>
                              {i.deviza
                                ? `${i.deviza.osszeg} ${i.deviza.devizanem}`
                                  + (i.deviza.arfolyam ? ` @ ${i.deviza.arfolyam}` : '')
                                : (i.tipus_kodolatlan || `típus ${i.tipus_kod}`)}
                            </span>
                          </div>)}
                      </td>
                    )}
                    {col.kozlemeny && (
                      <td style={td}>
                        {i.kozlemeny || null}
                        {(i.bizonylat_szama || i.hatarozatszam) && (
                          <div style={{fontSize:'var(--fs-xs)',color:'var(--text-dim)',
                            fontFamily:'var(--mono)'}}>
                            {i.bizonylat_szama || i.hatarozatszam}</div>)}
                      </td>
                    )}
                    {col.allapot && (
                      <td style={td}>
                        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                          {i.is_open && <Badge variant="warning">nyitott</Badge>}
                          {i.settled_at && <Badge variant="success">rendezve</Badge>}
                          {i.vanished_at && <Badge>a NAV kivette</Badge>}
                          {i.document_id && (
                            <a href={`/documents?id=${i.document_id}`}
                              style={{fontSize:'0.7rem',color:'var(--primary)',
                                display:'inline-flex',alignItems:'center',gap:3}}>
                              <ExternalLink size={11}/>bizonylat
                            </a>)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > LIMIT && (
          <div style={{display:'flex',gap:8,alignItems:'center',marginTop:12,
            justifyContent:'center'}}>
            <Btn size="sm" variant="ghost" disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}>Előző</Btn>
            <span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>
              {offset + 1}–{Math.min(offset + LIMIT, total)} / {total}
            </span>
            <Btn size="sm" variant="ghost" disabled={offset + LIMIT >= total}
              onClick={() => setOffset(offset + LIMIT)}>Következő</Btn>
          </div>
        )}
      </Card>
    </div>
  );
}
